import React, { useState, useEffect } from "react";
import {
  Button,
  Form,
  Input,
  Spin,
  Tabs,
  Modal,
  Card,
  Statistic,
  Row,
  Col,
  message,
} from "antd";
import {
  ArrowLeft,
  User,
  Phone,
  Mail,
  MapPin,
  Lock,
  Shield,
  Trash2,
  Edit,
  Calendar,
  Package,
  CreditCard,
  Trophy,
  Star,
} from "lucide-react";
import { API_METHODS } from "../../constants";
import useApi from "../../hooks/useApi";
import { isEmpty } from "../../generalFunctions";
import HeaderComponent from "./Home/HeaderComponent";
import Footer from "./Home/Footer";
import { useNavigate } from "react-router-dom";

const UserProfile = () => {
  const [profileForm] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const [activeTab, setActiveTab] = useState("profile");
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [deleteOtpSent, setDeleteOtpSent] = useState(false);
  const [deleteOtp, setDeleteOtp] = useState("");
  const [messageApi, contextHolder] = message.useMessage();
  const navigate = useNavigate();
  // API hooks
  const [profileResponse, profileRequest] = useApi(
    "/user/profile",
    null,
    API_METHODS.GET
  );
  const [updateResponse, updateRequest] = useApi(
    "/user/profile",
    null,
    API_METHODS.PUT
  );
  const [passwordResponse, passwordRequest] = useApi(
    "/user/profile/change-password",
    null,
    API_METHODS.PUT
  );
  const [deleteResponse, deleteRequest] = useApi(
    "/user/profile/delete-account",
    null,
    API_METHODS.POST
  );
  const [deleteOtpResponse, deleteOtpRequest] = useApi(
    "/user/profile/delete-account/otp",
    null,
    API_METHODS.POST
  );
  const [statsResponse, statsRequest] = useApi(
    "/user/profile/stat",
    null,
    API_METHODS.GET
  );

  useEffect(() => {
    // Check authentication
    const authToken = localStorage.getItem("authToken");
    if (!authToken) {
      messageApi.open({
        type: "info",
        content: `🙏 Please login to view your profile.`,
        duration: 2,
      });
      navigate("/login");
      return;
    }

    // Fetch profile data and stats
    profileRequest(null, API_METHODS.GET, "/user/profile");
    statsRequest(null, API_METHODS.GET, "/user/profile/stat");
  }, []);

  // Set form values when profile data loads
  useEffect(() => {
    if (
      !isEmpty(profileResponse) &&
      !profileResponse.isLoading &&
      profileResponse.data
    ) {
      const userData = profileResponse.data;
      profileForm.setFieldsValue({
        first_name: userData.first_name,
        last_name: userData.last_name,
        email: userData.email,
        phone: userData.phone,
        gotra: userData.gotra,
        gan: userData.gan,
        address: userData.address,
        city: userData.city,
        state: userData.state,
        pincode: userData.pincode,
      });
    }
  }, [profileResponse, profileForm]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(amount || 0);
  };

  const handleProfileUpdate = async (values) => {
    try {
      await updateRequest(values, API_METHODS.PUT, "/user/profile");

      // Check if update was successful
      if (
        !isEmpty(updateResponse) &&
        !updateResponse.isLoading &&
        !updateResponse.isError
      ) {
        messageApi.open({
          type: "success",
          content: `✅ Profile updated successfully!`,
          duration: 2,
        });

        // Refresh profile data
        profileRequest(null, API_METHODS.GET, "/user/profile");
      }
    } catch (error) {
      console.error("Profile update error:", error);
    }
  };

  const handlePasswordChange = async (values) => {
    try {
      await passwordRequest(
        values,
        API_METHODS.PUT,
        "/user/profile/change-password"
      );

      // Check if password change was successful
      if (
        !isEmpty(passwordResponse) &&
        !passwordResponse.isLoading &&
        !passwordResponse.isError
      ) {
        messageApi.open({
          type: "success",
          content: `✅ Password changed successfully!`,
          duration: 2,
        });
        passwordForm.resetFields();
      }
    } catch (error) {
      console.error("Password change error:", error);
    }
  };

  const closeDeleteModal = () => {
    setIsDeleteModalVisible(false);
    setDeleteOtpSent(false);
    setDeleteOtp("");
  };

  // Step 1 — send the confirmation code to the registered mobile number
  const handleSendDeleteOtp = async () => {
    try {
      await deleteOtpRequest(
        null,
        API_METHODS.POST,
        "/user/profile/delete-account/otp"
      );
      setDeleteOtpSent(true);
      messageApi.open({
        type: "success",
        content: `📲 Confirmation code sent to your registered mobile number.`,
        duration: 3,
      });
    } catch (error) {
      console.error("Deletion OTP error:", error);
    }
  };

  // Step 2 — verify the code and delete
  const handleDeleteAccount = async () => {
    if (!/^\d{6}$/.test(deleteOtp.trim())) {
      messageApi.open({
        type: "info",
        content: `Please enter the 6-digit code sent to your mobile number`,
        duration: 2,
      });
      return;
    }

    const confirmDelete = window.confirm(
      "⚠️ WARNING: This action cannot be undone!\n\nDeleting your account will:\n• Remove all your personal data\n• Cancel any pending orders\n• Remove access to your order history\n\nAre you absolutely sure you want to delete your account?"
    );

    if (confirmDelete) {
      try {
        await deleteRequest(
          { otp: deleteOtp.trim() },
          API_METHODS.POST,
          "/user/profile/delete-account"
        );

        // Check if deletion was successful
        if (
          !isEmpty(deleteResponse) &&
          !deleteResponse.isLoading &&
          !deleteResponse.isError
        ) {
          messageApi.open({
            type: "success",
            content: `✅ Account deleted successfully. You will be redirected to home page.`,
            duration: 2,
          });

          localStorage.clear();
          navigate("/");
        }
      } catch (error) {
        console.error("Account deletion error:", error);
      }
    }
  };

  // Loading state
  if (profileResponse?.isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-red-50 flex items-center justify-center">
        <div className="text-center">
          <Spin size="large" />
          <p className="mt-4 text-lg text-gray-600">Loading your profile...</p>
        </div>
      </div>
    );
  }

  const profileData = profileResponse?.data;
  const statsData = statsResponse?.data;

  const tabItems = [
    {
      key: "profile",
      label: (
        <span className="flex items-center">
          <User className="h-4 w-4 mr-2" />
          Profile Information
        </span>
      ),
      children: (
        <div className="bg-white rounded-xl p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-semibold text-gray-800">
              Personal Information
            </h3>
            <Edit className="h-5 w-5 text-orange-500" />
          </div>

          <Form
            onFinish={handleProfileUpdate}
            className="space-y-4"
            form={profileForm}
            layout="vertical"
          >
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="First Name"
                  name="first_name"
                  rules={[
                    { required: true, message: "First name is required" },
                  ]}
                >
                  <Input
                    size="large"
                    placeholder="Enter your first name"
                    prefix={<User className="h-4 w-4 text-gray-400" />}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Last Name"
                  name="last_name"
                  rules={[{ required: true, message: "Last name is required" }]}
                >
                  <Input
                    size="large"
                    placeholder="Enter your last name"
                    prefix={<User className="h-4 w-4 text-gray-400" />}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="Email Address" name="email">
                  <Input
                    size="large"
                    disabled
                    prefix={<Mail className="h-4 w-4 text-gray-400" />}
                    className="bg-gray-50"
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Phone Number"
                  name="phone"
                  rules={[
                    {
                      pattern: /^[6-9]\d{9}$/,
                      message: "Enter valid 10-digit phone number",
                    },
                  ]}
                >
                  <Input
                    size="large"
                    placeholder="Enter phone number"
                    prefix={<Phone className="h-4 w-4 text-gray-400" />}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="Gotra (Optional)" name="gotra">
                  <Input size="large" placeholder="Enter your gotra" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Gan (Optional)" name="gan">
                  <Input size="large" placeholder="Enter your gan" />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item label="Address" name="address">
              <Input.TextArea
                rows={3}
                placeholder="Enter your complete address"
              />
            </Form.Item>

            <Row gutter={16}>
              <Col span={8}>
                <Form.Item label="City" name="city">
                  <Input
                    size="large"
                    placeholder="City"
                    prefix={<MapPin className="h-4 w-4 text-gray-400" />}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="State" name="state">
                  <Input
                    size="large"
                    placeholder="State"
                    prefix={<MapPin className="h-4 w-4 text-gray-400" />}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  label="PIN Code"
                  name="pincode"
                  rules={[
                    {
                      pattern: /^\d{6}$/,
                      message: "Enter valid 6-digit PIN code",
                    },
                  ]}
                >
                  <Input
                    size="large"
                    placeholder="PIN Code"
                    prefix={<MapPin className="h-4 w-4 text-gray-400" />}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                loading={updateResponse?.isLoading}
                className="bg-gradient-to-r from-orange-500 to-red-500 border-none"
              >
                <Edit className="h-4 w-4 mr-2" />
                Update Profile
              </Button>
            </Form.Item>
          </Form>
        </div>
      ),
    },
    // Improved Change Password Tab - Replace the password tab content

    {
      key: "password",
      label: (
        <span className="flex items-center">
          <Lock className="h-4 w-4 mr-2" />
          Change Password
        </span>
      ),
      children: (
        <div className="bg-white rounded-xl p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-semibold text-gray-800">
              Password Security
            </h3>
            <Shield className="h-5 w-5 text-orange-500" />
          </div>

          <Form
            form={passwordForm}
            onFinish={handlePasswordChange}
            layout="vertical"
            className="max-w-2xl"
          >
            <Form.Item
              label="Current Password"
              name="current_password"
              rules={[
                { required: true, message: "Current password is required" },
              ]}
            >
              <Input.Password
                size="large"
                placeholder="Enter current password"
                prefix={<Lock className="h-4 w-4 text-gray-400" />}
              />
            </Form.Item>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="New Password"
                  name="new_password"
                  rules={[
                    { required: true, message: "New password is required" },
                    {
                      min: 6,
                      message: "Password must be at least 6 characters",
                    },
                  ]}
                >
                  <Input.Password
                    size="large"
                    placeholder="Enter new password"
                    prefix={<Lock className="h-4 w-4 text-gray-400" />}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Confirm New Password"
                  name="confirm_password"
                  dependencies={["new_password"]}
                  rules={[
                    {
                      required: true,
                      message: "Please confirm your new password",
                    },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue("new_password") === value) {
                          return Promise.resolve();
                        }
                        return Promise.reject(
                          new Error("Passwords do not match")
                        );
                      },
                    }),
                  ]}
                >
                  <Input.Password
                    size="large"
                    placeholder="Confirm new password"
                    prefix={<Lock className="h-4 w-4 text-gray-400" />}
                  />
                </Form.Item>
              </Col>
            </Row>

            {/* Password Requirements Info */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <h4 className="font-medium text-blue-800 mb-3 flex items-center">
                <Shield className="h-4 w-4 mr-2" />
                Password Requirements:
              </h4>
              <Row gutter={16}>
                <Col span={12}>
                  <ul className="text-sm text-blue-700 space-y-2">
                    <li className="flex items-center">
                      <div className="w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
                      At least 6 characters long
                    </li>
                    <li className="flex items-center">
                      <div className="w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
                      Different from current password
                    </li>
                  </ul>
                </Col>
                <Col span={12}>
                  <ul className="text-sm text-blue-700 space-y-2">
                    <li className="flex items-center">
                      <div className="w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
                      Use a strong, unique password
                    </li>
                    <li className="flex items-center">
                      <div className="w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
                      Avoid common words or patterns
                    </li>
                  </ul>
                </Col>
              </Row>
            </div>

            {/* Security Tips */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <h4 className="font-medium text-green-800 mb-3 flex items-center">
                <Shield className="h-4 w-4 mr-2" />
                💡 Security Tips:
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-green-700">
                <div className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span>Use a mix of letters, numbers, and symbols</span>
                </div>
                <div className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span>Don't reuse passwords from other sites</span>
                </div>
                <div className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span>Consider using a password manager</span>
                </div>
                <div className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span>Change your password regularly</span>
                </div>
              </div>
            </div>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                loading={passwordResponse?.isLoading}
                className="bg-gradient-to-r from-orange-500 to-red-500 border-none hover:from-orange-600 hover:to-red-600"
                icon={<Shield className="h-4 w-4" />}
              >
                Change Password
              </Button>
            </Form.Item>
          </Form>
        </div>
      ),
    },
    {
      key: "stats",
      label: (
        <span className="flex items-center">
          <Trophy className="h-4 w-4 mr-2" />
          My Statistics
        </span>
      ),
      children: (
        <div className="space-y-6">
          {/* Order Statistics */}
          <Card title="📊 Order Statistics" className="rounded-xl">
            <Row gutter={16}>
              <Col span={6}>
                <Statistic
                  title="Total Orders"
                  value={statsData?.order_statistics?.total_orders || 0}
                  prefix={<Package className="h-4 w-4 text-blue-500" />}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="Completed"
                  value={statsData?.order_statistics?.completed_orders || 0}
                  prefix={<Star className="h-4 w-4 text-green-500" />}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="Active Orders"
                  value={statsData?.order_statistics?.active_orders || 0}
                  prefix={<Calendar className="h-4 w-4 text-orange-500" />}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="Total Spent"
                  value={formatCurrency(
                    statsData?.order_statistics?.total_spent
                  )}
                  prefix={<CreditCard className="h-4 w-4 text-purple-500" />}
                />
              </Col>
            </Row>
          </Card>

          {/* Favorite Services */}
          {statsData?.favorite_services?.length > 0 && (
            <Card title="🏆 Your Favorite Services" className="rounded-xl">
              <div className="space-y-3">
                {statsData.favorite_services.map((service, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <span className="font-medium text-gray-800 capitalize">
                      {service.type.replace("_", " ")}
                    </span>
                    <span className="text-orange-600 font-bold">
                      {service.booking_count} bookings
                    </span>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Upcoming Bookings */}
          {statsData?.upcoming_bookings > 0 && (
            <Card title="📅 Upcoming Bookings" className="rounded-xl">
              <div className="text-center p-4">
                <div className="text-3xl font-bold text-orange-600 mb-2">
                  {statsData.upcoming_bookings}
                </div>
                <p className="text-gray-600">
                  You have {statsData.upcoming_bookings} upcoming puja booking
                  {statsData.upcoming_bookings > 1 ? "s" : ""}
                </p>
                <Button
                  type="primary"
                  className="mt-4 bg-gradient-to-r from-orange-500 to-red-500 border-none"
                  onClick={() => navigate("/orders")}
                >
                  View Orders
                </Button>
              </div>
            </Card>
          )}
        </div>
      ),
    },
    // {
    //   key: "danger",
    //   label: (
    //     <span className="flex items-center text-red-600">
    //       <Trash2 className="h-4 w-4 mr-2" />
    //       Danger Zone
    //     </span>
    //   ),
    //   children: (
    //     <div className="bg-white rounded-xl p-6 border-2 border-red-200">
    //       <div className="flex items-center justify-between mb-6">
    //         <h3 className="text-xl font-semibold text-red-600">Danger Zone</h3>
    //         <Trash2 className="h-5 w-5 text-red-500" />
    //       </div>

    //       <div className="bg-red-50 border border-red-200 rounded-lg p-6">
    //         <h4 className="font-semibold text-red-800 mb-3">Delete Account</h4>
    //         <p className="text-red-700 mb-4">
    //           Permanently delete your account and all associated data. This
    //           action cannot be undone.
    //         </p>

    //         <div className="bg-red-100 border border-red-300 rounded-lg p-4 mb-4">
    //           <h5 className="font-medium text-red-800 mb-2">
    //             ⚠️ This will permanently:
    //           </h5>
    //           <ul className="text-sm text-red-700 space-y-1">
    //             <li>• Delete all your personal information</li>
    //             <li>• Remove access to your order history</li>
    //             <li>• Cancel any pending orders</li>
    //             <li>• Clear your cart and saved preferences</li>
    //           </ul>
    //         </div>

    //         <Button
    //           danger
    //           size="large"
    //           icon={<Trash2 className="h-4 w-4" />}
    //           onClick={() => setIsDeleteModalVisible(true)}
    //           loading={deleteResponse?.isLoading}
    //         >
    //           Delete My Account
    //         </Button>
    //       </div>
    //     </div>
    //   ),
    // },
  ];

  return (
    <>
      {contextHolder}
      <HeaderComponent />
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-red-50 py-12 px-4">
        <div className="container mx-auto max-w-6xl">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <button
              onClick={() => navigate("/")}
              className="flex items-center text-gray-600 hover:text-orange-600 transition-all"
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
              Back to Home
            </button>
            <div className="text-center">
              <h1 className="text-3xl font-bold text-gray-800 flex items-center">
                <div className="w-10 h-10 bg-gradient-to-r from-orange-500 to-red-500 rounded-full flex items-center justify-center text-white mr-3">
                  🪔
                </div>
                My Profile
              </h1>
              <p className="text-gray-600">
                Manage your account settings and preferences
              </p>
            </div>
            <div></div>
          </div>

          {/* Profile Header Card */}
          {profileData && (
            <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-2xl p-8 mb-8">
              <div className="flex items-center">
                <div className="w-20 h-20 bg-white bg-opacity-20 rounded-full flex items-center justify-center text-3xl mr-6">
                  {profileData.first_name?.charAt(0)?.toUpperCase() || "U"}
                </div>
                <div>
                  <h2 className="text-2xl font-bold">
                    {profileData.first_name} {profileData.last_name}
                  </h2>
                  <p className="opacity-90">{profileData.email}</p>
                  <p className="text-sm opacity-75">
                    Member since{" "}
                    {new Date(profileData.created_on).getFullYear()}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Tabs */}
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <Tabs
              activeKey={activeTab}
              onChange={setActiveTab}
              items={tabItems}
              className="p-6"
              size="large"
            />
          </div>
        </div>
      </div>

      {/* Delete Account Modal */}
      <Modal
        title="⚠️ Confirm Account Deletion"
        open={isDeleteModalVisible}
        onCancel={closeDeleteModal}
        footer={[
          <Button key="cancel" onClick={closeDeleteModal}>
            Cancel
          </Button>,
          !deleteOtpSent ? (
            <Button
              key="send"
              type="primary"
              danger
              loading={deleteOtpResponse?.isLoading}
              onClick={handleSendDeleteOtp}
            >
              Send Confirmation Code
            </Button>
          ) : (
            <Button
              key="delete"
              type="primary"
              danger
              loading={deleteResponse?.isLoading}
              onClick={handleDeleteAccount}
            >
              Delete Account
            </Button>
          ),
        ]}
      >
        <div className="space-y-4">
          {!deleteOtpSent ? (
            <p className="text-gray-700">
              To confirm account deletion, we will send a 6-digit code to your
              registered mobile number on WhatsApp.
            </p>
          ) : (
            <>
              <p className="text-gray-700">
                Enter the 6-digit code sent to your registered mobile number:
              </p>
              <Input
                size="large"
                maxLength={6}
                inputMode="numeric"
                placeholder="6-digit code"
                value={deleteOtp}
                onChange={(e) =>
                  setDeleteOtp(e.target.value.replace(/\D/g, "").slice(0, 6))
                }
                prefix={<Lock className="h-4 w-4 text-gray-400" />}
              />
              <Button
                type="link"
                className="px-0"
                loading={deleteOtpResponse?.isLoading}
                onClick={handleSendDeleteOtp}
              >
                Resend code
              </Button>
            </>
          )}
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-red-700 text-sm">
              <strong>Warning:</strong> This action is permanent and cannot be
              undone.
            </p>
          </div>
        </div>
      </Modal>

      <Footer />
    </>
  );
};

export default UserProfile;
